import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import AttachFileIcon from '@mui/icons-material/AttachFile'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

import { BillingReviewStatus, Consultation, ConsultationStatus } from '@/core/models/consultation.model'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'

import ConsultationPaymentDialog, { useConsultationPaymentDialog } from './ConsultationPaymentDialog'

type Props = {
  consultation: Consultation
}

/**
 * Archive-side billing entry point. Visible only when the consultation is tied
 * to a billing-enabled flow (paymentDialogConfig, desmosActStatus, or a
 * non-pending billingReview) and not cancelled. Reuses ConsultationPaymentDialog
 * for the full interaction — this card is a summary + opener.
 *
 * Reads the consultation via RTK Query rather than the prop so that the
 * updateBillingReview mutation's cache update (see consultationApi) flows
 * through here without a page refresh.
 */
export default function BillingReviewCard({ consultation: consultationFromProps }: Props) {
  const { t } = useTranslation()
  const { open, consultationId, showDialog, hideDialog } = useConsultationPaymentDialog()

  const { data: consultationFromQuery } = useGetConsultationQuery(consultationFromProps._id)
  const consultation = consultationFromQuery ?? consultationFromProps

  const waitingRoomId = typeof consultation.waitingRoom === 'string' ? consultation.waitingRoom : undefined
  const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId ?? '', { skip: !waitingRoomId })

  const billingReview = consultation.billingReview
  const desmosActStatus = consultation.desmosActStatus

  const reviewTouched =
    !!billingReview &&
    (billingReview.status !== BillingReviewStatus.Pending ||
      !!billingReview.issueReason ||
      !!billingReview.note ||
      (billingReview.justificationFileKeys?.length ?? 0) > 0)

  const shouldShow =
    consultation.status !== ConsultationStatus.Canceled &&
    (waitingRoom?.paymentDialogConfig?.enabled || !!desmosActStatus || reviewTouched)

  if (!shouldShow) return null

  const status = billingReview?.status ?? BillingReviewStatus.Pending
  const justificationCount = billingReview?.justificationFileKeys?.length ?? 0
  const desmosActs = desmosActStatus?.acts ?? []
  const desmosErrorCount = desmosActs.filter((a) => !a.success).length

  const renderStatusChip = () => {
    if (status === BillingReviewStatus.Completed) {
      return (
        <Chip
          size="small"
          color="success"
          icon={<CheckCircleIcon />}
          label={t('page.consultation.paymentDialog.billingReview.status.completed', { defaultValue: 'Terminé' })}
        />
      )
    }
    if (status === BillingReviewStatus.Issue) {
      return (
        <Chip
          size="small"
          color="error"
          icon={<ErrorIcon />}
          label={t('page.consultation.paymentDialog.billingReview.status.issue', { defaultValue: 'Problème' })}
        />
      )
    }
    return (
      <Chip
        size="small"
        color="warning"
        icon={<HourglassEmptyIcon />}
        label={t('page.consultation.paymentDialog.billingReview.status.pending', { defaultValue: 'En attente' })}
      />
    )
  }

  const renderDesmosChip = () => {
    if (!desmosActStatus) return null
    if (desmosErrorCount > 0) {
      return (
        <Chip
          size="small"
          variant="outlined"
          color="error"
          icon={<ErrorIcon />}
          label={t('page.consultation.paymentDialog.desmos.status.partial', {
            defaultValue: '{{count}} acte(s) en erreur',
            count: desmosErrorCount,
          })}
        />
      )
    }
    if (desmosActs.length === 0) return null
    return (
      <Chip
        size="small"
        variant="outlined"
        color="success"
        icon={<CheckCircleIcon />}
        label={t('page.consultation.paymentDialog.desmos.status.success', { defaultValue: 'Sent to Desmos' })}
      />
    )
  }

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
            <ReceiptLongIcon color="action" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('page.consultation.billingCard.title', { defaultValue: 'Facturation' })}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5, rowGap: 0.5 }}>
                {renderStatusChip()}
                {billingReview?.issueReason && status === BillingReviewStatus.Issue && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="error"
                    label={t(`page.consultation.paymentDialog.billingReview.reasons.${billingReview.issueReason}`)}
                  />
                )}
                {justificationCount > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<AttachFileIcon />}
                    label={t('page.consultation.billingCard.justifications', {
                      defaultValue: '{{count}} justificatif(s)',
                      count: justificationCount,
                    })}
                  />
                )}
                {renderDesmosChip()}
              </Stack>
              {billingReview?.note && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {billingReview.note}
                </Typography>
              )}
            </Box>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={() => showDialog(consultation._id)}
          >
            {t('page.consultation.billingCard.open', { defaultValue: 'Ouvrir la facturation' })}
          </Button>
        </Stack>
      </Paper>
      <ConsultationPaymentDialog
        open={open}
        consultationId={consultationId}
        onClose={hideDialog}
        consultation={consultation}
      />
    </>
  )
}
